// src/components/Locales.tsx
import { ReactNode, useEffect, useState } from 'react';
import { TranslationProvider } from 'contexts/TranslationContext';
import useConfig from 'hooks/useConfig';
import { I18n } from 'types/config';

type LocaleMessages = Record<string, string | Record<string, any>>;

const loadLocaleData = (locale: I18n): Promise<{ default: LocaleMessages }> => {
  switch (locale) {
    case 'fr':
      return import('utils/locales/fr.json');
    case 'ro':
      return import('utils/locales/ro.json');
    case 'zh':
      return import('utils/locales/zh.json');
    case 'en':
    default:
      return import('utils/locales/en.json');
  }
};

const flattenMessages = (nestedMessages: LocaleMessages, prefix = ''): Record<string, string> => {
  return Object.keys(nestedMessages).reduce((messages, key) => {
    const value = nestedMessages[key];
    const prefixedKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      messages[prefixedKey] = value;
    } else {
      Object.assign(messages, flattenMessages(value, prefixedKey));
    }

    return messages;
  }, {} as Record<string, string>);
};

interface Props {
  children: ReactNode;
}

const Locales = ({ children }: Props) => {
  const { i18n } = useConfig();
  const [messages, setMessages] = useState<Record<string, string> | undefined>();

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const { default: loadedMessages } = await loadLocaleData(i18n);
        const flattenedMessages = flattenMessages(loadedMessages);
        setMessages(flattenedMessages);
      } catch (error) {
        console.error('Error loading locale data:', error);
        setMessages(undefined);
      }
    };

    fetchMessages();
  }, [i18n]);

  return <>{messages ? <TranslationProvider messages={messages}>{children}</TranslationProvider> : <div>Loading...</div>}</>;
};

export default Locales;
